// @PoweredByAi(date="2026-06-09", model="claude-sonnet-4-6")
import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {MatSelectChange} from '@angular/material/select';
import {validateInteger} from '../../../commons/validators';
import {downloadBlob, errorCode, isDefined} from '../../../commons/commons';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {first} from 'rxjs/operators';
import {ConfigurationService} from '../../../shared/configuration.service';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {DATE_FORMATS} from '../../../i18n/i18n-defaults';
import {SmagApiService} from '../../../api/smag-api.service';
import {DialogData} from '../ec-measures.component';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {ErrorCode, GseErrorCode} from '../../../api/errors';
import {HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, Subject} from 'rxjs';
import {SpinnerData} from 'src/app/shared/spinner/spinner.component';
import {ECMeasuresTemplateType} from 'smag-commons/cfg/hierarchy';
import {ECDistributorEnergyFileType} from 'smag-commons/dto/api/templates';

export type UploadMode = 'template' | 'distributor';


const MAX_FILE_MB = 2;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CSV_EXTENSION = '.csv';
// CSV mime types are reported inconsistently across browsers/OSes (text/csv,
// application/vnd.ms-excel, application/csv or even empty), so for CSV we accept
// by extension and only hint mime types in the file picker.
const CSV_ACCEPT = '.csv,text/csv,application/vnd.ms-excel';

type ExpectedFileType = 'xlsx' | 'csv';

interface Template {
    name: string;
    file: File;
}

export interface TimeRange {
    fromTime: number; // epoch in milliseconds
    toTime: number; // epoch in milliseconds
    includesNow: boolean; // whether this range is something like "Last ... or ... so far" ("hourly" ranges do *not* include now by definition)
}

export interface DateBoundaries {
    minDateTime: Moment; // inclusive
    maxDateTime: Moment; // inclusive
    delta?: number;
    deltaUoT?: string;
}

const DIALOG_WIDTH = 700;
// @PoweredByAi(date="2026-06-22", model="claude-sonnet-4-6")

@Component({
    selector: 'app-measures-upload',
    templateUrl: './measures-upload.component.html',
    styleUrls: ['./measures-upload.component.scss'],
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS}
    ]
})
export class MeasuresUploadComponent implements OnInit {

    @Input() resource: DialogData;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    form: FormGroup;
    customTimeRangeForm: FormGroup;
    currentStep: number;
    TIME_RANGES: { [key: string]: number };
    MINUTE_OPTIONS = [0, 15, 30, 45];
    XLSX_MIME_TYPE: string = XLSX_MIME_TYPE;
    CSV_ACCEPT: string = CSV_ACCEPT;
    MAX_FILE_MB: number = MAX_FILE_MB;
    templateChosen: boolean;
    templateDownloaded: boolean;
    fileUploaded;
    template: Template;
    uploadMode: UploadMode | null = null;
    distributorFileAbsorbed: Template = {name: null, file: null};
    distributorFileInjected: Template = {name: null, file: null};
    private timeZone: string;
    private boundaries: DateBoundaries;
    private selectedMinutesInterval: number;


    constructor(private readonly dialogService: DialogService,
                private readonly configurationService: ConfigurationService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private fb: FormBuilder) {

        this.TIME_RANGES = {
            Hourly: 60,
            'Quarterly hour': 15
        };
        this.currentStep = 1;
        this.templateChosen = false;
        this.templateDownloaded = false;
        this.template = {name: null, file: null};
        this.customTimeRangeForm = this.fb.group({
            startDate: new FormControl(null, [Validators.required]),
            startHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)])),
            startMinute: new FormControl(0, [Validators.required]),
            endDate: new FormControl(null, [Validators.required]),
            endHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)])),
            endMinute: new FormControl(0, [Validators.required]),
        }, {
            validators: [ctrl => this.validateCustomTimeRange(ctrl)]
        });
        this.form = this.fb.group({
            timeRange: new FormControl(null, [Validators.required])
        });
        this.timeZone = null;
        this.selectedMinutesInterval = null;
        this.fileUploaded = false;
    }

    ngOnInit(): void {
        this.configurationService.timeConfig.pipe(
            first()
        ).subscribe(timeCfg => {
            this.init(timeCfg.time_zone);
        });
    }


    private init(timeZone: string) {
        this.timeZone = timeZone;
        const delta = 2;
        const deltaUoT = 'years';
        const now = moment.tz(timeZone);
        const min = now.clone().startOf(deltaUoT).subtract(delta, deltaUoT);
        // date boundaries
        this.boundaries = {
            minDateTime: min,
            maxDateTime: now,
            delta,
            deltaUoT
        };

    }

    get maxDate(): Moment {
        return this.boundaries.maxDateTime.clone().startOf('day');
    }

    get minDate(): Moment {
        return this.boundaries.minDateTime.clone().startOf('day');
    }

    get startMinDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value.clone().startOf('day').subtract(this.boundaries.delta, this.boundaries.deltaUoT) :
            this.minDate;
    }

    get startMaxDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value :
            this.boundaries.maxDateTime.clone().endOf('day');
    }

    get endMinDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            this.customTimeRangeForm.controls.startDate.value :
            this.minDate;
    }

    get endMaxDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            moment.min([this.maxDate, this.customTimeRangeForm.controls.startDate.value.clone()
                .startOf('day').add(this.boundaries.delta, this.boundaries.deltaUoT)]) :
            this.maxDate;
    }

    get STEPS(): string[] {
        if (this.uploadMode === 'template') {
            return ['Choose mode', 'Choose time unit', 'Choose Period', 'Download template', 'Upload file'];
        }
        if (this.uploadMode === 'distributor') {
            return this.isProsumerResource
                ? ['Choose mode', 'Upload absorbed energy file', 'Upload injected energy file']
                : ['Choose mode', 'Upload file'];
        }
        return ['Choose mode'];
    }

    get isProsumerResource(): boolean {
        return this.resource?.type === ECMeasuresTemplateType.PROSUMER;
    }

    get isLastStepValid(): boolean {
        if (this.uploadMode === 'template') {
            return this.templateChosen;
        }
        if (this.uploadMode === 'distributor') {
            return this.isProsumerResource ? !!this.distributorFileInjected.name : !!this.distributorFileAbsorbed.name;
        }
        return false;
    }

    get isTimeUnitValid(): boolean {
        return this.form.get('timeRange').valid;
    }

    get isPeriodValid(): boolean {
        return this.customTimeRangeForm.valid;
    }


    isNextEnabled(step: number) {
        switch (step) {
            case 1:
                return this.uploadMode !== null;
            case 2:
                if (this.uploadMode === 'template') {
                    return this.isTimeUnitValid; // granularity must be chosen first
                }
                if (this.uploadMode === 'distributor') {
                    // non-prosumer: step 2 is the last step, Next is never shown (Submit is shown instead)
                    return this.isProsumerResource ? !!this.distributorFileAbsorbed.name : false;
                }
                return false;
            case 3:
                if (this.uploadMode === 'template') {
                    return this.isPeriodValid; // then the period form
                }
                return false;
            case 4:
                if (this.uploadMode === 'template') {
                    return true; // download step
                }
                return false;
            default:
                return false;
        }
    }

    // @PoweredByAi(date="2026-06-24", model="claude-opus-4-5")
    onSelectMode(mode: UploadMode) {
        this.uploadMode = mode;
        this.nextStep();
    }

    nextStep() {
        this.currentStep++;
    }

    prevStep() {
        this.currentStep--;
    }

    mkTimeRange(): TimeRange {
        const start = (this.customTimeRangeForm.controls.startDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.startHour.value)
            .minutes(this.customTimeRangeForm.controls.startMinute.value ?? 0)
            .seconds(0).milliseconds(0);
        const end = (this.customTimeRangeForm.controls.endDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.endHour.value)
            .minutes(this.customTimeRangeForm.controls.endMinute.value ?? 0)
            .seconds(0).milliseconds(0);
        const startValue = start.valueOf();
        const endValue = end.valueOf();
        const now = moment.tz(this.timeZone).valueOf();
        return {
            fromTime: startValue,
            toTime: endValue,
            includesNow: now >= startValue && now <= endValue
        } as TimeRange;
    }


    onClickSend() {
        if (this.uploadMode === 'distributor') {
            this.sendItDistributor();
        } else {
            this.sendIt();
        }
    }

    // @PoweredByAi(date="2026-06-24", model="claude-opus-4-8")
    // Maps the single (non-injected) distributor file to its energy-file type by resource type:
    // PV plant -> PRODUCED, producer pod -> INJECTED, consumer pod -> CONSUMED, prosumer pod -> CONSUMED (+ INJECTED, added below).
    private get absorbedFileType(): ECDistributorEnergyFileType {
        switch (this.resource.type) {
            case ECMeasuresTemplateType.PLANT:
                return ECDistributorEnergyFileType.PRODUCED;
            case ECMeasuresTemplateType.PRODUCER:
                return ECDistributorEnergyFileType.INJECTED;
            default:
                return ECDistributorEnergyFileType.CONSUMED;
        }
    }

    sendItDistributor() {
        const uploads: { type: ECDistributorEnergyFileType, file: { content: Blob, filename: string } }[] = [
            {
                type: this.absorbedFileType,
                file: {content: this.distributorFileAbsorbed.file, filename: this.distributorFileAbsorbed.name}
            }
        ];
        if (this.isProsumerResource) {
            uploads.push({
                type: ECDistributorEnergyFileType.INJECTED,
                file: {content: this.distributorFileInjected.file, filename: this.distributorFileInjected.name}
            });
        }
        const spinner = this.openSpinner('We\'re uploading your measures.\n You\'ll see them shortly.');
        uploads.reduce(
            (chain, {type, file}) => chain.then(() => this.apiService.uploadDistributorMeasures(this.resource.resourceId, type, file)),
            Promise.resolve())
            .then(() => {
                this.fileUploaded = true;
                this.nextStep();
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('File correctly uploaded: measures will arrive shortly'),
                });
            })
            .catch(error => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Error during document upload'),
                    abort: true
                });
                if (errorCode(error) === ErrorCode.INVALID_FILE_TYPE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid template file for chosen resource'));
                } else if (errorCode(error) === ErrorCode.INVALID_DISTRIBUTOR_FILE_TYPE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid distributor file type for the chosen resource'));
                } else if (errorCode(error) === ErrorCode.UNSUPPORTED_DISTRIBUTOR_FILE_TYPE) {
                    this.msgBoxService.error(this.i18n.translate('Unsupported distributor file type for the chosen resource'));
                } else if (errorCode(error) === ErrorCode.CANNOT_UPLOAD_MEASURES_DISTRIBUTORS_FILE) {
                    this.msgBoxService.error(this.i18n.translate('An error occurred while uploading the distributor measures file'));
                } else if (error instanceof HttpErrorResponse && (error as HttpErrorResponse).status === 413) {
                    this.msgBoxService.error(this.i18n.translate('File size must be below {0} MB', MAX_FILE_MB));
                } else {
                    this.msgBoxService.error(this.i18n.translate('An unexpected error occurred while uploading the file'));
                }
            });
    }

    sendIt() {
        const timeRange = this.mkTimeRange();
        const spinner = this.openSpinner('We\'re uploading your measures.\n You\'ll see them shortly.');
        this.apiService.uploadMeasuresTemplateExcel(this.resource.resourceId,
            timeRange.fromTime,
            timeRange.toTime,
            this.selectedMinutesInterval,
            this.resource.type,
            {content: this.template.file, filename: this.template.name})
            .then(() => {
                this.fileUploaded = true;
                this.nextStep();
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('File correctly uploaded: measures will arrive shortly'),
                });
            })
            .catch(error => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Error during document upload'),
                    abort: true
                });
                if (errorCode(error) === ErrorCode.INVALID_MEASURE) {
                    this.msgBoxService.error(this.i18n.translate('File contains invalid measures'));
                } else if (errorCode(error) === ErrorCode.INVALID_NEGATIVE_MEASURE) {
                    this.msgBoxService.error(this.i18n.translate('File contains negative values'));
                } else if (errorCode(error) === ErrorCode.DATES_NOT_MATCH_TIME_INTERVAL) {
                    this.msgBoxService.error(this.i18n.translate('Dates provided inside the file do not match the specified interval'));
                } else if (errorCode(error) === ErrorCode.UNSUPPORTED_TIME_INTERVAL) {
                    this.msgBoxService.error(this.i18n.translate('You specified an invalid time interval'));
                } else if (errorCode(error) === ErrorCode.INVALID_TIME_RANGE) {
                    this.msgBoxService.error(this.i18n.translate('The file provided contains an invalid time range'));
                } else if (errorCode(error) === ErrorCode.MEASURE_UPLOAD_TIME_RANGE_TOO_SHORT) {
                    this.msgBoxService.error(this.i18n.translate('The time range must be at least 1 hour long'));
                } else if (errorCode(error) === ErrorCode.INVALID_FILE_TYPE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid template file for chosen resource'));
                } else if (errorCode(error) === GseErrorCode.INVALID_GSE_FILE) {
                    this.msgBoxService.error(this.i18n.translate('Invalid GSE file'));
                } else if (error instanceof HttpErrorResponse && (error as HttpErrorResponse).status === 413) {
                    this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', this.template.name, MAX_FILE_MB));
                } else {
                    this.msgBoxService.error(this.i18n.translate('An unexpected error occurred while uploading the file'));
                }
            });

    }

    get isQuarterly(): boolean {
        return this.selectedMinutesInterval === 15;
    }

    onSelectTimeFilter($event: MatSelectChange) {
        this.selectedMinutesInterval = $event.value;
        if ($event.value !== 15) {
            this.customTimeRangeForm.patchValue({startMinute: 0, endMinute: 0});
        }
    }

    onClickDownloadTemplate() {

        const timeRange = this.mkTimeRange();
        this.apiService.downloadMeasuresTemplateExcel(this.resource.resourceId,
            timeRange.fromTime,
            timeRange.toTime,
            this.selectedMinutesInterval,
            this.resource.type)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateDownloaded = true;
            }, error => {
                this.msgBoxService.error(this.i18n.translate('There was an error while downloading the file'));
            });

    }

    onClickUploadTemplate($event: Event) {
        const picked = this.pickFile($event);
        if (picked) {
            this.template = picked;
            this.templateChosen = true;
        }
    }

    private pickFile($event: Event, expected: ExpectedFileType = 'xlsx'): Template | null {
        const tg = $event.target as HTMLInputElement;
        if (tg.files?.length) {
            const file = tg.files[0];
            return this.validateFile(file, expected) ? {name: file.name, file} : null;
        }
        return null;
    }

    private validateFile(file: File, expected: ExpectedFileType): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (expected === 'csv') {
            if (!file.name.toLowerCase().endsWith(CSV_EXTENSION)) {
                this.msgBoxService.error(this.i18n.translate('File {0} is not a CSV file', file.name));
                return false;
            }
        } else if (file.type !== XLSX_MIME_TYPE) {
            this.msgBoxService.error(this.i18n.translate('File {0} is not an XLSX file', file.name));
            return false;
        }
        return true;
    }

    onClickTrashTemplate(fileSelect: HTMLInputElement) {
        fileSelect.value = null; // allows file re-selection
        this.template = {name: null, file: null};
        this.templateChosen = false;
    }

    onClickUploadDistributorAbsorbed($event: Event) {
        const picked = this.pickFile($event, 'csv');
        if (picked) { this.distributorFileAbsorbed = picked; }
    }

    onClickTrashDistributorAbsorbed(fileSelect: HTMLInputElement) {
        fileSelect.value = null;
        this.distributorFileAbsorbed = {name: null, file: null};
    }

    onClickUploadDistributorInjected($event: Event) {
        const picked = this.pickFile($event, 'csv');
        if (picked) { this.distributorFileInjected = picked; }
    }

    onClickTrashDistributorInjected(fileSelect: HTMLInputElement) {
        fileSelect.value = null;
        this.distributorFileInjected = {name: null, file: null};
    }

    get startMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.startDate.value &&
        this.customTimeRangeForm.controls.startDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.startDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.startDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    get startMaxMinute(): number {
        const now = moment.tz(this.timeZone);
        const startDate = this.customTimeRangeForm.controls.startDate.value;
        if (startDate && startDate.isSame(now, 'day') &&
            this.customTimeRangeForm.controls.startHour.value === this.boundaries.maxDateTime.hour()) {
            return Math.floor(this.boundaries.maxDateTime.minute() / 15) * 15;
        }
        return 45;
    }

    get endMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.endDate.value &&
        this.customTimeRangeForm.controls.endDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.endDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.endDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    get endMaxMinute(): number {
        const now = moment.tz(this.timeZone);
        const endDate = this.customTimeRangeForm.controls.endDate.value;
        if (endDate && endDate.isSame(now, 'day') &&
            this.customTimeRangeForm.controls.endHour.value === this.boundaries.maxDateTime.hour()) {
            return Math.floor(this.boundaries.maxDateTime.minute() / 15) * 15;
        }
        return 45;
    }

    get minHour(): number {
        return 0;
    }

    private startTimeInRange(hour: number, minute: number): boolean {
        if (hour < this.minHour || hour > this.startMaxHour) { return false; }
        if (hour === this.startMaxHour && minute > this.startMaxMinute) { return false; }
        return true;
    }

    private endTimeInRange(hour: number, minute: number): boolean {
        if (hour < this.minHour || hour > this.endMaxHour) { return false; }
        if (hour === this.endMaxHour && minute > this.endMaxMinute) { return false; }
        return true;
    }


    // @PoweredByAi(date="2026-06-24", model="claude-opus-4-5", prompt="Invalidate quarterly-hour ranges whose from/to delta is shorter than 1h")
    private validateCustomTimeRange(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        if (ctrls.startDate.value && ctrls.endDate.value && isDefined(ctrls.startHour.value) && isDefined(ctrls.endHour.value)) {
            const startMinute = ctrls.startMinute.value ?? 0;
            const endMinute = ctrls.endMinute.value ?? 0;
            if (!fromBeforeTo(ctrls.startDate.value, ctrls.startHour.value, startMinute,
                              ctrls.endDate.value, ctrls.endHour.value, endMinute)) {
                errs.fromdateAfterTodate = true;
            }
            // check consistency of dates and times (hour+minute combined)
            // we cannot entirely rely on min/max of <input>, as one could still enter a valid value and *then* change the date
            if (!this.startTimeInRange(ctrls.startHour.value, startMinute)) {
                errs.startHourOutOfRange = true;
            }
            if (!this.endTimeInRange(ctrls.endHour.value, endMinute)) {
                errs.endHourOutOfRange = true;
            }
            // on quarterly-hour resolution a range must span at least 1 hour: reject deltas strictly shorter than 60 minutes
            if (this.isQuarterly && !errs.fromdateAfterTodate &&
                rangeMinutes(ctrls.startDate.value, ctrls.startHour.value, startMinute,
                             ctrls.endDate.value, ctrls.endHour.value, endMinute) < 60) {
                errs.quarterlyRangeTooShort = true;
            }
        }
        return Object.values(errs).every(err => !err) ? null : errs;
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }


}

function mkTime(date: Moment, hour: number, minute: number = 0): Moment {
    return date.clone().hour(hour).minute(minute).second(0).millisecond(0);
}

function fromBeforeTo(startDate: Moment, startHour: number, startMinute: number,
                      endDate: Moment, endHour: number, endMinute: number): boolean {
    const startDateHour: Moment = mkTime(startDate, startHour, startMinute);
    const endDateHour: Moment = mkTime(endDate, endHour, endMinute);
    return startDateHour.isBefore(endDateHour);
}

// @PoweredByAi(date="2026-06-24", model="claude-opus-4-5")
function rangeMinutes(startDate: Moment, startHour: number, startMinute: number,
                      endDate: Moment, endHour: number, endMinute: number): number {
    const startDateHour: Moment = mkTime(startDate, startHour, startMinute);
    const endDateHour: Moment = mkTime(endDate, endHour, endMinute);
    return endDateHour.diff(startDateHour, 'minutes');
}
